/**
 * Applies edit-lock state to the shared Plan Actions menu, used by both the
 * Calendar and Metric toolbars.
 *
 * Menu items are plain config objects rather than components, so they cannot
 * inherit any ambient disabled state and must be gated explicitly here.
 *
 * The `edit_plan` entry is decorated in place when the customer config declares
 * it, and injected as a leading section when it does not. Injecting matters:
 * with the lock enabled but no entry point, every plan would be permanently
 * read-only.
 *
 * Sections in, sections out. Dividers are derived downstream by
 * `flattenMenuSections`, so the injected entry becomes its own section rather
 * than an item plus a hand-placed rule.
 */
import type { ReactNode } from 'react';

import { tpoOptionTestId } from '../../../config/testIds';
import {
    EDIT_PLAN_MENU_KEY,
    type PlanActionMenuItem,
    type PlanActionMenuSections,
} from '../../../config/planActionMenuConfig';

export { EDIT_PLAN_MENU_KEY };

/**
 * Actions that stay available without the lock. These either only read
 * (`show_changes`, `optimisation_results`, `view_trend_chart`, `export`,
 * `compare_scenarios`) or create a separate plan rather than mutating this one
 * (`save_as`, `duplicate_plan`).
 */
const VIEW_MODE_ALLOWED_KEYS = new Set<string>([
    EDIT_PLAN_MENU_KEY,
    'show_changes',
    'optimisation_results',
    'view_trend_chart',
    'compare_scenarios',
    'export',
    'save_as',
    'duplicate_plan',
]);

interface ApplyEditLockOptions {
    canEdit: boolean;
    isLockEnabled: boolean;
    isHeldByOther: boolean;
    /**
     * Operational plans are read-only: the `edit_plan` entry stays visible so
     * the action is discoverable, but it cannot acquire a lock.
     */
    isOperationalPlan?: boolean;
    /**
     * Rendered label for the `edit_plan` entry. The caller owns it (see
     * `EditPlanMenuLabel`) so the holder tooltip lives in JSX and this file stays
     * a plain input-to-output function.
     */
    editLabel: ReactNode;
    /** Base id the menu's option test ids derive from. */
    baseTestId: string;
}

const isKeyedItem = (
    item: PlanActionMenuItem,
): item is Extract<PlanActionMenuItem, { key?: unknown }> & { key: string } =>
    !!item && 'key' in item && typeof (item as { key?: unknown }).key === 'string';

export function applyEditLockToMenu(
    sections: PlanActionMenuSections,
    { canEdit, isLockEnabled, isHeldByOther, isOperationalPlan = false, editLabel, baseTestId }: ApplyEditLockOptions,
): PlanActionMenuSections {
    if (!isLockEnabled) return sections;

    const hasEditItem = sections.some((section) =>
        section.some((item) => isKeyedItem(item) && item.key === EDIT_PLAN_MENU_KEY),
    );

    // Unavailable when someone else holds it (the label's tooltip says who),
    // when this user already holds it (there is nothing left to acquire), and
    // when the plan is operational (calendar/data `is_operational_plan`).
    const isEditItemDisabled = isHeldByOther || canEdit || isOperationalPlan;
    const editItem = {
        key: EDIT_PLAN_MENU_KEY,
        label: editLabel,
        disabled: isEditItemDisabled,
        'data-testid': tpoOptionTestId(baseTestId, EDIT_PLAN_MENU_KEY),
    } as PlanActionMenuItem;

    const gated = sections.map((section) =>
        section.map((item) => {
            if (!isKeyedItem(item)) return item;

            if (item.key === EDIT_PLAN_MENU_KEY) {
                // Configured explicitly: keep its position and any config extras.
                return { ...item, label: editLabel, disabled: isEditItemDisabled };
            }

            const isConfigDisabled = 'disabled' in item && !!item.disabled;
            const blockedByLock = !canEdit && !VIEW_MODE_ALLOWED_KEYS.has(item.key);
            return { ...item, disabled: isConfigDisabled || blockedByLock };
        }),
    );

    // While editing there is nothing to acquire, so an injected entry would only
    // add noise. A configured one keeps its slot and simply reads as disabled.
    if (canEdit || hasEditItem) return gated;

    return [[editItem], ...gated];
}
